import type {
  RaspadinhaCampaign,
  RaspadinhaParticipant,
} from '@/types/raspadinha-campaign'
import { collectCampaignWinners, type RaspadinhaCampaignWinnerRow } from './collect-campaign-winners'
import { formatWinningTimeLabel } from './date-winning-schedules'

export type RaspadinhaParticipantRole = 'ganhador' | 'participante'

export type RaspadinhaCampaignParticipantRow = {
  participant: RaspadinhaParticipant
  role: RaspadinhaParticipantRole
  prizeLabel: string | null
  wonDateLabel: string | null
  wonTimeLabel: string | null
}

export function normalizeParticipantDocument(document: string): string {
  return document.replace(/\D/g, '')
}

function normalizeDocument(document: string): string {
  return normalizeParticipantDocument(document)
}

export function findParticipantByDocument(
  participants: RaspadinhaParticipant[],
  document: string
): RaspadinhaParticipant | null {
  const normalizedDocument = normalizeDocument(document)
  if (!normalizedDocument) return null

  return (
    participants.find(
      (participant) => normalizeDocument(participant.document) === normalizedDocument
    ) ?? null
  )
}

function buildWinnerIndex(
  winnerRows: RaspadinhaCampaignWinnerRow[]
): Map<string, RaspadinhaCampaignWinnerRow> {
  const index = new Map<string, RaspadinhaCampaignWinnerRow>()

  for (const row of winnerRows) {
    const normalizedDocument = normalizeDocument(row.winner.document)
    if (!normalizedDocument || index.has(normalizedDocument)) continue
    index.set(normalizedDocument, row)
  }

  return index
}

export function ensureCampaignParticipants(
  participants: RaspadinhaParticipant[] | undefined
): RaspadinhaParticipant[] {
  if (!participants?.length) return []
  return participants.map((participant) => ({ ...participant }))
}

export function collectCampaignParticipants(
  campaign: Pick<RaspadinhaCampaign, 'participants' | 'winning_date_schedules'>
): RaspadinhaCampaignParticipantRow[] {
  const participants = ensureCampaignParticipants(campaign.participants)
  const winnerRows = collectCampaignWinners(campaign.winning_date_schedules)
  const winnerIndex = buildWinnerIndex(winnerRows)

  const rows = participants.map((participant) => {
    const winnerMatch = winnerIndex.get(normalizeDocument(participant.document))

    if (winnerMatch) {
      return {
        participant,
        role: 'ganhador' as const,
        prizeLabel: winnerMatch.winner.prize_label || null,
        wonDateLabel: winnerMatch.dateLabel,
        wonTimeLabel: formatWinningTimeLabel(winnerMatch.slotTime),
      }
    }

    return {
      participant,
      role: 'participante' as const,
      prizeLabel: null,
      wonDateLabel: null,
      wonTimeLabel: null,
    }
  })

  return rows.sort((left, right) => {
    const leftTime = new Date(left.participant.participated_at).getTime()
    const rightTime = new Date(right.participant.participated_at).getTime()

    if (Number.isFinite(leftTime) && Number.isFinite(rightTime) && leftTime !== rightTime) {
      return rightTime - leftTime
    }

    return left.participant.name.localeCompare(right.participant.name, 'pt-BR', {
      sensitivity: 'base',
    })
  })
}

export function formatParticipantDateTime(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'

  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date)
}
